// resources/js/src/components/community/components/profile/AllFriendsModal.tsx
import { useEffect, useCallback, useState } from "react";
import { Link } from "react-router";
import { profileApi } from "../../../../../services/profile.api";
import type { ProfileFriend } from "../../../../../services/profile.api";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  initialFriends?: ProfileFriend[];
};

export default function AllFriendsModal({
  isOpen,
  onClose,
  userId,
  initialFriends = [],
}: Props) {
  const [friends, setFriends] = useState<ProfileFriend[]>(initialFriends);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // reset لما الـ modal يتفتح
  useEffect(() => {
    if (!isOpen) return;
    setFriends(initialFriends);
    setCursor(null);
    setHasMore(true);
    setError("");
  }, [isOpen, userId]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    setError("");
    try {
      const res = await profileApi.getFriends(userId, cursor, 15);
      setFriends((prev) => [...prev, ...res.data]);
      setCursor(res.meta.next_cursor);
      setHasMore(res.meta.has_more);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load friends");
    } finally {
      setLoading(false);
    }
  }, [userId, cursor, hasMore, loading]);

  // أول تحميل
  useEffect(() => {
    if (isOpen && friends.length === 0) {
      loadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div className="relative bg-white dark:bg-deepSlate rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            All Friends
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {friends.length === 0 && !loading && (
            <p className="text-center text-gray-500 py-8">No friends yet.</p>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {friends.map((friend) => (
              <Link
                key={friend.id}
                to={`/community/profile/${friend.id}`}
                onClick={onClose}
                className="flex flex-col items-center text-center p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 group"
              >
                <div className="w-20 h-20 rounded-full overflow-hidden mb-2 bg-gray-100">
                  {friend.avatarUrl ? (
                    <img
                      src={friend.avatarUrl}
                      alt={friend.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xl font-semibold text-gray-400">
                      {friend.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <h3 className="font-medium text-gray-900 dark:text-white text-sm truncate w-full">
                  {friend.name}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-xs">
                  {friend.mutualFriends > 0
                    ? `${friend.mutualFriends} mutual friends`
                    : "\u00A0"}
                </p>
              </Link>
            ))}
          </div>

          {loading && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
            </div>
          )}

          {error && (
            <p className="text-center text-red-500 py-4">{error}</p>
          )}

          {hasMore && !loading && (
            <button
              onClick={loadMore}
              className="w-full mt-4 py-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg font-medium"
            >
              Load More
            </button>
          )}

          {!hasMore && friends.length > 0 && (
            <p className="text-center text-gray-400 text-sm mt-4">
              You've reached the end.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}